import { Component, computed, linkedSignal, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";
import { DateTimeControlComponent } from "../date-input";

export interface DateTimeEditorOptions<TData> {
  readValue: (data: TData) => string | Date;
  writeValue: (data: TData, value: string | Date) => Promise<string | Date>;
}

export type DateTimeEditorParams<TData> = ICellEditorParams<TData> & DateTimeEditorOptions<TData>;

@Component({
  selector: "ag-grid-date-time-editor",
  template: ` <hawk-shared-date-time-control [ngModel]="inputValue()" (ngModelChange)="handleValueChanged($event)" /> `,

  imports: [FormsModule, DateTimeControlComponent],
})
export class AgGridDateTimeEditorComponent<TData> implements ICellEditorAngularComp {
  public static options<TData>(options: DateTimeEditorOptions<TData>) {
    return options;
  }

  protected params = signal<DateTimeEditorParams<TData>>(null);
  protected inputValue = computed(() => {
    const params = this.params();
    let value = params.value;

    if (params.readValue) {
      value = params.readValue(params.data);
    }
    if (value instanceof Date) {
      value = value.toJSON();
    }
    return value;
  });
  protected outputValue = linkedSignal<string>(() => this.inputValue());

  public agInit(params: DateTimeEditorParams<TData>) {
    this.params.set(params);
  }

  public refresh(params: DateTimeEditorParams<TData>) {
    this.params.set(params);
    return true;
  }

  public getValue() {
    const value = this.outputValue();
    if (this.inputValue() !== value && this.params().writeValue) {
      this.params().writeValue(this.params().data, value);
    }
    return value;
  }

  public isPopup(): boolean {
    return true;
  }

  public getPopupPosition() {
    return "under" as const;
  }

  protected handleValueChanged(value: string | Date) {
    if (value instanceof Date) {
      value = value.toJSON();
    }
    this.outputValue.set(value);
  }

  protected async handleCancelClicked() {
    this.params().stopEditing();
  }
}
